// ============================================================================
// EmptyStatePill — standard empty-state surface across the template
// ============================================================================
// Renders a small banner-style pill describing why a grid / dropdown / list
// has no data. Use this INSTEAD of hardcoded sample arrays.
//
// Per the workspace MEMORY.md rule "No mock/fallback data in UI", every list
// surface must show real data or a clear empty state — never mocked values.
//
// USAGE:
//   {isEndpointMissing && (
//     <EmptyStatePill
//       variant="endpoint-pending"
//       endpoint="/me/api-keys"
//     />
//   )}
//
//   {accessibleEntities.length === 0 && (
//     <EmptyStatePill
//       variant="empty"
//       title="No accessible entities"
//       message="Contact your administrator to assign you an entity."
//     />
//   )}

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';

type Variant =
  | 'endpoint-pending'   // Backend endpoint not yet implemented (404)
  | 'empty'              // Endpoint works but returned zero rows
  | 'error';             // Endpoint returned an error other than 404

interface Props {
  variant: Variant;
  /** Required for `endpoint-pending` variant. */
  endpoint?: string;
  /** Override the default title. */
  title?: string;
  /** Override the default message. */
  message?: string;
  /** Optional refresh handler — when present, renders a "Try again" button. */
  onRefresh?: () => void;
  /** Wrapper class passthrough. Useful for placing inside grids vs free-floating. */
  className?: string;
  style?: React.CSSProperties;
}

const DEFAULTS: Record<Variant, { title: string; message: string }> = {
  'endpoint-pending': {
    title: 'Endpoint not yet implemented',
    message: 'When the backend lands this surface will populate. Until then there is nothing to show.',
  },
  'empty': {
    title: 'No data',
    message: 'Nothing to show here yet.',
  },
  'error': {
    title: 'Could not load data',
    message: 'Something went wrong. Try again, or contact your administrator if the problem persists.',
  },
};

export const EmptyStatePill: React.FC<Props> = ({
  variant,
  endpoint,
  title,
  message,
  onRefresh,
  className,
  style,
}) => {
  const defaults = DEFAULTS[variant];
  const Icon = variant === 'error' ? IconSystem.alert
            : variant === 'endpoint-pending' ? IconSystem.info
            : IconSystem.info;

  // For endpoint-pending, weave the endpoint path into the message for context.
  const effectiveMessage = (variant === 'endpoint-pending' && endpoint)
    ? `GET ${endpoint} is not yet implemented on the server. ${message ?? defaults.message}`
    : (message ?? defaults.message);

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.625rem',
        padding: '0.75rem 1rem',
        margin: '0 1rem',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        fontSize: '0.8125rem',
        color: 'var(--text-secondary)',
        ...style,
      }}
      data-empty-state-variant={variant}
    >
      <Icon size={16} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.125rem' }}>
          {title ?? defaults.title}
        </div>
        <div>{effectiveMessage}</div>
      </div>
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          style={{
            background: 'transparent',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            padding: '0.25rem 0.625rem',
            fontSize: '0.75rem',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Try again
        </button>
      )}
    </div>
  );
};

export default EmptyStatePill;
